import { useMemo } from 'react'
import { Token, TokenAmount } from '@etcswapv2/sdk-legacy'
import ERC20_INTERFACE from '../constants/abis/erc20'
import { useMultipleContractSingleData, EthersInterface } from '../state/multicall/hooks'

/**
 * Returns the token allowance for a given owner/spender pair.
 *
 * Uses useMultipleContractSingleData with ERC20_INTERFACE directly
 * instead of useSingleCallResult with a viem contract, because viem
 * contracts don't have the .interface property that multicall hooks expect.
 *
 * @param token - The token to check allowance for
 * @param owner - The address that owns the tokens
 * @param spender - The address that would spend the tokens
 * @returns TokenAmount representing allowance, or undefined if loading/error
 */
export function useTokenAllowance(
  token?: Token,
  owner?: string,
  spender?: string
): TokenAmount | undefined {
  // Build addresses array - empty if no token
  const addresses = useMemo(() => (token ? [token.address] : []), [token])

  // Build inputs for allowance(owner, spender) call
  const inputs = useMemo(() => [owner, spender], [owner, spender])

  // Use the working pattern: pass Interface directly instead of contract
  const results = useMultipleContractSingleData(
    addresses,
    ERC20_INTERFACE as unknown as EthersInterface,
    'allowance',
    inputs
  )

  // Get the first (and only) result
  const allowanceResult = results[0]
  const allowance = allowanceResult?.result?.[0]

  return useMemo(() => {
    if (!token || !allowance) return undefined
    return new TokenAmount(token, allowance.toString())
  }, [token, allowance])
}
